/**
 * 고정폭 표(DBMS_XPLAN 등)에서 **넓은 글자(한글·한자·전각)를 정확히 2칸**으로 그린다 (2026-10-05).
 *
 * 표는 한글 한 글자를 2칸으로 세어 정렬한다. 그런데 고정폭 글꼴(Consolas 등)에 한글이 없으면 브라우저가
 * 다른 글꼴로 대신 그리고, 그 글자 폭이 2칸보다 넓어 **한글이 든 줄만 뒤가 밀렸다**(실행계획의 Name 칸).
 * 글꼴 설치에 기대지 않고, 넓은 글자를 `2ch` 상자에 넣어 칸을 강제한다. 텍스트 자체는 바꾸지 않는다(복사 그대로).
 */
import React from 'react';

/** 화면 폭이 2칸인 글자 — 백엔드 database/xplanNames.js 의 displayWidth 와 같은 범위 */
export const isWide = (ch) => {
  const c = ch.codePointAt(0);
  return (c >= 0x1100 && c <= 0x115F) || (c >= 0x2E80 && c <= 0xA4CF) || (c >= 0xAC00 && c <= 0xD7A3)
    || (c >= 0xF900 && c <= 0xFAFF) || (c >= 0xFE30 && c <= 0xFE4F) || (c >= 0xFF00 && c <= 0xFF60)
    || (c >= 0xFFE0 && c <= 0xFFE6) || (c >= 0x20000 && c <= 0x3FFFD);
};

/**
 * @param {string} line
 * @param {string} wideClass  넓은 글자 상자 클래스(`display:inline-block; width:2ch`)
 * @returns {string|Array}  넓은 글자가 없으면 문자열 그대로(렌더 비용 없음)
 */
export function monoWide(line, wideClass) {
  const s = String(line ?? '');
  if (!/[^ -~]/.test(s)) return s;   // 출력 가능한 ASCII 뿐이면 그대로
  const out = [];
  let plain = '';
  let k = 0;
  for (const ch of s) {
    if (isWide(ch)) {
      if (plain) { out.push(plain); plain = ''; }
      out.push(<span key={k++} className={wideClass}>{ch}</span>);
    } else {
      plain += ch;
    }
  }
  if (plain) out.push(plain);
  return out;
}

/**
 * 여러 줄 글 전체에 monoWide 를 건다 — `<pre>` 안에 그대로 넣는다. 넓은 글자가 하나도 없으면 원문 문자열 그대로(렌더 비용 없음).
 * 상자 클래스 기본값은 전역 `mono-wide`(styles/appModal.css) — 실행계획 탭은 자기 CSS 모듈의 `wideCh` 를 쓴다.
 */
export function monoWideText(text, wideClass = 'mono-wide') {
  const s = String(text ?? '');
  if (!/[^\n -~]/.test(s)) return s;
  return s.split('\n').map((line, i, all) => (
    // eslint-disable-next-line react/no-array-index-key -- 줄 순서가 곧 키다(글이 바뀌면 통째로 다시 그린다)
    <React.Fragment key={i}>{monoWide(line, wideClass)}{i < all.length - 1 ? '\n' : ''}</React.Fragment>
  ));
}
